import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import OAuthButtons from '../components/auth/OAuthButtons';
import AuthStage from '../components/auth/AuthStage';
import PhoneAppOffers from '../components/auth/PhoneAppOffer';
import PikBrand from '../components/brand/PikBrand';
import { PikLogo } from '../components/brand/PikLogo';
import InviterContextBanner from '../components/auth/InviterContextBanner';
import { peekPendingGroup } from '../lib/groupContinue';
import { consumePendingShare, postSignInPath } from '../lib/shareContinue';
import { PHONE_APP_COPY, emailLooksCaptured, phoneOfferMode, usePhoneAppOffer } from '../lib/phoneAppOffer';
import { clearScanSignupDraft, consumePendingInvite, scanDraftFields } from '../lib/scanSignupDraft';
import API from '../lib/api';
import { clearSignupRequestId, setupFailureMessage, signupRequestId } from '../lib/setupRequest';

const formatPhone = (val) => {
  let d = (val || '').replace(/\D/g, '');
  if (d.length === 11 && d.startsWith('1')) d = d.slice(1);
  d = d.slice(0, 10);
  const p1 = d.slice(0, 3);
  const p2 = d.slice(3, 6);
  const p3 = d.slice(6, 10);
  let out = '';
  if (p1) out = `(${p1}`;
  if (p1.length === 3) out += ') ';
  if (p2) out += p2;
  if (p2.length === 3 && p3) out += '-';
  if (p3) out += p3;
  return out;
};

function signupDraft() {
  return scanDraftFields() || {};
}

function readWaitlistPref() {
  try {
    const raw = sessionStorage.getItem('signup_waitlist_pref');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      ios: parsed.ios === true,
      android: parsed.android === true,
    };
  } catch {
    return null;
  }
}

function initialWaitlist(platform) {
  const pref = readWaitlistPref();
  if (!pref?.[platform]) return false;
  return emailLooksCaptured(signupDraft().email);
}

const formatApiErrorDetail = (detail) => {
  if (detail == null) return null;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(' ');
  }
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export default function SignupPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [firstName, setFirstName] = useState(() => signupDraft().first_name || '');
  const [lastName, setLastName] = useState(() => signupDraft().last_name || '');
  const [email, setEmail] = useState(() => signupDraft().email || '');
  const [phone, setPhone] = useState(() => formatPhone(signupDraft().phone || ''));
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  // Unconfirmed until the person joins and an email is already captured.
  // A configured store URL replaces only that platform's button.
  const [iosWaitlist, setIosWaitlist] = useState(() => initialWaitlist('ios'));
  const [androidWaitlist, setAndroidWaitlist] = useState(() => initialWaitlist('android'));
  useEffect(() => {
    try {
      sessionStorage.removeItem('signup_waitlist_pref');
    } catch {
      /* private mode */
    }
  }, []);
  const phoneOffer = usePhoneAppOffer();
  const iosWaitlistMode = phoneOffer.ready && phoneOfferMode(phoneOffer.appStoreUrl).mode === 'waitlist';
  const androidWaitlistMode = phoneOffer.ready && phoneOfferMode(phoneOffer.playStoreUrl).mode === 'waitlist';
  // Session 126t (fix #2) — persistent inline error state. Toast-only
  // feedback was disappearing in 3 seconds and users were missing the
  // duplicate-email rejection entirely. We now render a sticky alert
  // beneath the form too, cleared the moment the user touches any
  // input (signaling they're correcting it).
  const [error, setError] = useState('');
  // Session 126r — inviter context for /signup?from=invite|share. The
  // share token is dropped into sessionStorage by PublicProfilePage
  // when an un-authed visitor taps the Add CTA, then the SignupPage
  // (here) renders a tiny "<inviter> invited you" banner so the user
  // remembers WHY they're signing up. Boosts invite-flow conversion.
  const fromParam = searchParams.get('from');
  const pendingToken = typeof window !== 'undefined'
    ? sessionStorage.getItem('pending_share_token')
    : null;
  const showInviterContext = Boolean(pendingToken)
    && (fromParam === 'invite' || fromParam === 'share');

  const setField = (setter) => (v) => {
    // Any keystroke clears the inline error so it doesn't sit there
    // forever after a successful correction.
    setter(v);
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your first and last name');
      toast.error('Please enter your first and last name');
      return;
    }
    const phoneDigits = phone.replace(/\D/g, '');
    if (phoneDigits.length < 7) {
      setError('Please enter a valid phone number');
      toast.error('Please enter a valid phone number');
      return;
    }
    // Session 126t (fix #8) — bumped min length 6 → 8 to align with
    // the security spec + match the backend's stricter requirement
    // when the new test_session126r_status_contract gets extended.
    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      const draft = scanDraftFields() || {};
      const created = await register({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        email: (email || '').trim().toLowerCase(),
        phone: phone.trim(),
        password,
        ios_waitlist: iosWaitlistMode ? iosWaitlist : false,
        android_waitlist: androidWaitlistMode ? androidWaitlist : false,
        company: draft.company || '',
        job_title: draft.job_title || '',
        city: draft.city || '',
        region: draft.region || '',
        met_where: draft.met_where || '',
        met_when: draft.met_when || '',
        met_how: draft.met_how || '',
        met_via: draft.met_via || '',
        met_city: draft.met_city || '',
        met_region: draft.met_region || '',
        signup_request_id: signupRequestId(),
      });
      if (created?.mfa_required && created?.mfa_token) {
        navigate(`/login?mfa=1&mfa_token=${encodeURIComponent(created.mfa_token)}`, { replace: true });
        return;
      }
      clearSignupRequestId();
      toast.success('Account created!');
      // Session 125a — if the user arrived via an invite link, auto-save
      // the inviter as their first contact so onboarding feels seamless.
      try {
        const pending = consumePendingShare();
        if (pending?.token) {
          const { profileShareAPI } = await import('../lib/api');
          const hwm = pending.hwm || {};
          await profileShareAPI.addToContacts(pending.token, {
            met_where: hwm.met_where || draft.met_where || '',
            met_when: hwm.met_when || draft.met_when || '',
            met_how: hwm.met_how || draft.met_how || '',
            met_via: hwm.met_via || draft.met_via || '',
            met_city: hwm.met_city || draft.met_city || '',
            met_region: hwm.met_region || draft.met_region || '',
          });
          toast.success('Inviter saved to your contacts');
        }
      } catch {
        // Non-fatal — the user can still find them via the invite link.
      }
      try {
        const inviteToken = consumePendingInvite();
        if (inviteToken) await API.post(`/invites/public/${inviteToken}/connect`);
      } catch {
        // The signup claim also opens this request when the email matches.
      }
      clearScanSignupDraft();
      const pendingGroup = peekPendingGroup();
      if (pendingGroup?.participantId) {
        try {
          const { groupShareSessionAPI } = await import('../lib/api');
          await groupShareSessionAPI.attachParticipant(pendingGroup.token, pendingGroup.participantId);
        } catch {
          // The session page retries this after the phone-apps step.
        }
      }
      // Leave a pending intro token in place. postSignInPath sends a new
      // account through /welcome/apps and back to /intro/{token}, where the
      // intro page saves once.
      navigate(postSignInPath(created));
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail)
        || setupFailureMessage(err, 'Could not create your account. What you typed is still here. Try again.');
      // Surface backend errors (e.g. "Email already registered") in
      // both the toast (immediate attention) and the persistent inline
      // alert (still visible after the toast vanishes).
      setError(detail);
      toast.error(detail);
    }
    setLoading(false);
  };

  const inputCls = 'h-12 bg-slate-50 border-transparent focus:border-nav-blue focus:bg-white transition-all rounded-lg';

  return (
    <AuthStage testId="signup-page">
      <div className="flex w-full flex-col gap-6" data-testid="signup-stack">
        <div className="text-center space-y-2 mb-6">
          <PikLogo className="h-24" testId="signup-pik-logo" />
          {showInviterContext ? (
            <>
              <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">
                {fromParam === 'invite'
                  ? <>You&rsquo;re invited to <PikBrand testId="signup-pik-brand" /></>
                  : <>Welcome to <PikBrand testId="signup-pik-brand" /></>}
              </h1>
              <p className="text-muted-foreground text-sm">
                {fromParam === 'invite'
                  ? 'Finish creating your account to connect.'
                  : 'Create your account to keep this profile.'}
              </p>
            </>
          ) : (
            <>
              <h1 className="font-heading text-3xl font-bold text-foreground tracking-tight">
                Join <PikBrand testId="signup-pik-brand" />
              </h1>
              <p className="text-muted-foreground text-sm">Start organizing your connections</p>
            </>
          )}
        </div>

        {showInviterContext && (
          <InviterContextBanner
            shareToken={pendingToken}
            variant={fromParam === 'invite' ? 'invite' : 'share'}
          />
        )}

        <PhoneAppOffers
          variant="signup"
          ready={phoneOffer.ready}
          appStoreUrl={phoneOffer.appStoreUrl}
          playStoreUrl={phoneOffer.playStoreUrl}
          iosJoined={iosWaitlist}
          androidJoined={androidWaitlist}
          emailDialog
          formEmail={email}
          onIosChange={setIosWaitlist}
          onAndroidChange={setAndroidWaitlist}
        />

        <div className="space-y-4 rounded-xl border border-border bg-slate-50/80 p-4" data-testid="signup-web-secondary">
          <p className="text-sm text-muted-foreground leading-snug">{PHONE_APP_COPY.webNow}</p>
          <OAuthButtons
            mode="signup"
            iosWaitlist={iosWaitlistMode && iosWaitlist}
            androidWaitlist={androidWaitlistMode && androidWaitlist}
            providerOrder={['apple', 'google']}
          />
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="signup-form">
          {error && (
            <div
              role="alert"
              className="rounded-lg border border-destructive/30 bg-destructive/5 text-destructive text-sm px-3 py-2.5 leading-snug flex items-start gap-2"
              data-testid="signup-error-message"
            >
              <span className="text-destructive/80 mt-0.5">!</span>
              <span className="flex-1">{error}</span>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label htmlFor="first-name" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">First Name</Label>
              <Input id="first-name" name="given-name" autoComplete="given-name" placeholder="Jane" value={firstName} onChange={(e) => setField(setFirstName)(e.target.value)} required className={inputCls} data-testid="signup-first-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="last-name" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Last Name</Label>
              <Input id="last-name" name="family-name" autoComplete="family-name" placeholder="Doe" value={lastName} onChange={(e) => setField(setLastName)(e.target.value)} required className={inputCls} data-testid="signup-last-name-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Email</Label>
            <Input id="email" name="email" autoComplete="email" type="email" inputMode="email" autoCapitalize="off" autoCorrect="off" spellCheck="false" placeholder="you@email.com" value={email} onChange={(e) => setField(setEmail)(e.target.value)} required className={inputCls} data-testid="signup-email-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Phone</Label>
            <Input id="phone" name="tel" autoComplete="tel" type="tel" inputMode="tel" placeholder="(555) 123-4567" value={phone} onChange={(e) => setField(setPhone)(formatPhone(e.target.value))} required className={inputCls} data-testid="signup-phone-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Password</Label>
            <div className="relative">
              <Input id="password" name="new-password" autoComplete="new-password" type={showPw ? 'text' : 'password'} placeholder="Min 8 characters" value={password} onChange={(e) => setField(setPassword)(e.target.value)} required className={`${inputCls} pr-10`} data-testid="signup-password-input" />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" data-testid="signup-toggle-password">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full h-12 bg-nav-blue hover:bg-nav-blue/90 text-white rounded-full font-heading font-semibold text-base shadow-lg shadow-nav-blue/20 transition-all" data-testid="signup-submit-btn">
            {loading ? 'Creating account...' : 'Create Account'}
          </Button>
        </form>
        </div>

        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link to="/login" className="text-nav-blue font-semibold hover:underline" data-testid="signup-login-link">Sign in</Link>
        </p>
      </div>
    </AuthStage>
  );
}
